import { v } from "convex/values";


export const reportStatus = v.union(
  v.literal("active"),
  v.literal("paused"),
  v.literal("archived"),
  v.literal("completed"),
);

export const reportVisibility = v.union(
  v.literal("public"), // Visible para todos en la empresa
  v.literal("private"), // Solo el creador
  v.literal("assigned"), // Solo usuarios asignados
);

export const reportFormat = v.union(
  v.literal("pdf"),
  v.literal("csv"),
  v.literal("json"),
  v.literal("excel"),
);

export const reportFrequency = v.union(
  v.literal("daily"),
  v.literal("weekly"),
  v.literal("monthly"),
  v.literal("custom"),
  v.literal("once"),
);

// Tipos de rango de fecha dinamico para reportes programados
export const dateRangeType = v.union(
  v.literal("fixed"), // Rango fijo (from/to especificos)
  v.literal("today"), // Hoy
  v.literal("yesterday"), // Ayer
  v.literal("last_7_days"), // Últimos 7 días
  v.literal("last_30_days"), // Últimos 30 días
  v.literal("this_week"), // Esta semana (lunes a hoy)
  v.literal("last_week"), // Semana pasada
  v.literal("this_month"), // Este mes
  v.literal("last_month"), // Mes pasado
  v.literal("this_quarter"), // Este trimestre
  v.literal("last_quarter"), // Trimestre pasado
  v.literal("this_year"), // Este año
  v.literal("last_year"), // Año pasado
  v.literal("custom_days"), // Últimos N días personalizados
);


export const filterOperator = v.union(
  v.literal("equals"),
  v.literal("notEquals"),
  v.literal("contains"),
  v.literal("startsWith"),
  v.literal("endsWith"),
  v.literal("greaterThan"),
  v.literal("lessThan"),
  v.literal("greaterThanOrEqual"),
  v.literal("lessThanOrEqual"),
  v.literal("between"),
  v.literal("in"),
  v.literal("notIn"),
);

export const aggregationOperation = v.union(
  v.literal("sum"),
  v.literal("avg"),
  v.literal("min"),
  v.literal("max"),
  v.literal("count"),
  v.literal("countDistinct"),
);

export const sortDirection = v.union(v.literal("asc"), v.literal("desc"));


export const filterValidator = v.object({
  field: v.string(),
  operator: filterOperator,
  value: v.any(),
  dateRange: v.optional(
    v.object({
      from: v.number(),
      to: v.number(),
    }),
  ),
  // Rango dinamico para reportes programados
  dynamicDateRange: v.optional(
    v.object({
      type: dateRangeType,
      customDays: v.optional(v.number()), // Para type="custom_days"
    }),
  ),
});

// Funcion helper para calcular el rango de fechas dinamico
export function calculateDynamicDateRange(
  type: string,
  customDays?: number,
  timezoneOffsetMinutes?: number,
): { from: number; to: number } {
  const offsetMs = (timezoneOffsetMinutes ?? -360) * 60 * 1000; // Default: UTC-6 (Honduras)
  const utcNow = Date.now();
  const localNow = new Date(utcNow + offsetMs);
  const localToday = new Date(
    localNow.getFullYear(),
    localNow.getMonth(),
    localNow.getDate(),
  );
  const localTodayEnd = new Date(
    localToday.getTime() + 24 * 60 * 60 * 1000 - 1,
  );

  // Convertir limites locales a UTC para comparar con timestamps UTC en DB
  const today = localToday.getTime() - offsetMs;
  const todayEnd = localTodayEnd.getTime() - offsetMs;

  switch (type) {
    case "today":
      return { from: today, to: todayEnd };

    case "yesterday": {
      const yesterday = today - 24 * 60 * 60 * 1000;
      const yesterdayEnd = today - 1;
      return { from: yesterday, to: yesterdayEnd };
    }

    case "last_7_days": {
      const from = today - 7 * 24 * 60 * 60 * 1000;
      return { from, to: todayEnd };
    }

    case "last_30_days": {
      const from = today - 30 * 24 * 60 * 60 * 1000;
      return { from, to: todayEnd };
    }

    case "this_week": {
      const dayOfWeek = localToday.getDay();
      const monday =
        today - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) * 24 * 60 * 60 * 1000;
      return { from: monday, to: todayEnd };
    }

    case "last_week": {
      const dayOfWeek = localToday.getDay();
      const thisMonday =
        today - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) * 24 * 60 * 60 * 1000;
      const lastMonday = thisMonday - 7 * 24 * 60 * 60 * 1000;
      const lastSunday = thisMonday - 1;
      return { from: lastMonday, to: lastSunday };
    }

    case "this_month": {
      const firstDayLocal = new Date(
        localNow.getFullYear(),
        localNow.getMonth(),
        1,
      );
      const from = firstDayLocal.getTime() - offsetMs;
      return { from, to: todayEnd };
    }

    case "last_month": {
      const firstDayLocal = new Date(
        localNow.getFullYear(),
        localNow.getMonth() - 1,
        1,
      );
      const lastDayLocal = new Date(
        localNow.getFullYear(),
        localNow.getMonth(),
        0,
        23,
        59,
        59,
        999,
      );
      const from = firstDayLocal.getTime() - offsetMs;
      const to = lastDayLocal.getTime() - offsetMs;
      return { from, to };
    }

    case "this_quarter": {
      const quarter = Math.floor(localNow.getMonth() / 3);
      const firstDayLocal = new Date(localNow.getFullYear(), quarter * 3, 1);
      const from = firstDayLocal.getTime() - offsetMs;
      return { from, to: todayEnd };
    }

    case "last_quarter": {
      const quarter = Math.floor(localNow.getMonth() / 3);
      const lastQ = quarter === 0 ? 3 : quarter - 1;
      const year =
        quarter === 0 ? localNow.getFullYear() - 1 : localNow.getFullYear();
      const firstDayLocal = new Date(year, lastQ * 3, 1);
      const lastDayLocal = new Date(year, lastQ * 3 + 3, 0, 23, 59, 59, 999);
      const from = firstDayLocal.getTime() - offsetMs;
      const to = lastDayLocal.getTime() - offsetMs;
      return { from, to };
    }

    case "this_year": {
      const firstDayLocal = new Date(localNow.getFullYear(), 0, 1);
      const from = firstDayLocal.getTime() - offsetMs;
      return { from, to: todayEnd };
    }

    case "last_year": {
      const firstDayLocal = new Date(localNow.getFullYear() - 1, 0, 1);
      const lastDayLocal = new Date(
        localNow.getFullYear() - 1,
        11,
        31,
        23,
        59,
        59,
        999,
      );
      const from = firstDayLocal.getTime() - offsetMs;
      const to = lastDayLocal.getTime() - offsetMs;
      return { from, to };
    }

    case "custom_days": {
      const days = customDays || 7;
      const from = today - days * 24 * 60 * 60 * 1000;
      return { from, to: todayEnd };
    }

    case "fixed":
    default:
      return { from: today, to: todayEnd };
  }
}

// Etiquetas en espanol para los tipos de rango dinamico
export const DATE_RANGE_TYPE_LABELS: Record<string, string> = {
  fixed: "Rango fijo",
  today: "Hoy",
  yesterday: "Ayer",
  last_7_days: "Últimos 7 días",
  last_30_days: "Últimos 30 días",
  this_week: "Esta semana",
  last_week: "Semana pasada",
  this_month: "Este mes",
  last_month: "Mes pasado",
  this_quarter: "Este trimestre",
  last_quarter: "Trimestre pasado",
  this_year: "Este año",
  last_year: "Año pasado",
  custom_days: "Últimos N días",
};

export const aggregationValidator = v.object({
  field: v.string(),
  operation: aggregationOperation,
});

export const sortValidator = v.object({
  field: v.string(),
  direction: sortDirection,
});

// Config por columna (opcional, keyed por columnId). Permite:
//  - displayField: para columnas de relación, qué campo de la entidad
export const columnConfigValidator = v.object({
  displayField: v.optional(v.string()),
  label: v.optional(v.string()),
});

export const reportConfigValidator = v.object({
  filters: v.array(filterValidator),
  aggregations: v.optional(v.array(aggregationValidator)),
  sorts: v.optional(v.array(sortValidator)),
  groupBy: v.optional(v.array(v.string())),
  columns: v.array(v.string()),
  columnConfigs: v.optional(v.record(v.string(), columnConfigValidator)),
});

export const scheduleValidator = v.object({
  frequency: v.optional(reportFrequency),
  time: v.optional(v.string()), // Formato "HH:mm"
  dayOfWeek: v.optional(v.number()), // 0-6 (domingo-sábado)
  dayOfMonth: v.optional(v.number()), // 1-31
  customCron: v.optional(v.string()), // Expresión cron custom
  timezone: v.optional(v.string()), // "America/Tegucigalpa"
});

export const distributionValidator = v.object({
  email: v.object({
    enabled: v.boolean(),
    recipients: v.optional(v.array(v.string())),
  }),
  notification: v.optional(v.boolean()),
  webhook: v.optional(
    v.object({
      enabled: v.boolean(),
      url: v.optional(v.string()),
    }),
  ),
  storage: v.optional(
    v.object({
      enabled: v.boolean(),
      path: v.optional(v.string()),
    }),
  ),
});


export const reportExecutionStatus = v.union(
  v.literal("running"),
  v.literal("completed"),
  v.literal("failed"),
);


export const notificationType = v.union(
  v.literal("report_ready"),
  v.literal("report_failed"),
  v.literal("report_shared"),
  v.literal("message_received"),
);
